/**
 * Endpoints de *Servicios* (`/api/servicios*`).
 *
 * El módulo exige el grupo `configuration` (el mismo que muestra la sección
 * Configuración). La prueba contra el controlador PTS puede tardar unos segundos
 * cuando el equipo no responde, por eso va en su propia consulta.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  EstadoComprobanteFiltro,
  OrigenComprobante,
  RespuestaBoleteo,
  RespuestaDocumentosServicios,
  RespuestaEstadoServicios,
  RespuestaPts,
} from './servicios.types';

export const serviciosKeys = {
  all: ['servicios'] as const,
  pts: ['servicios', 'pts'] as const,
  estado: ['servicios', 'estado'] as const,
  documentos: (origen: OrigenComprobante, estado: EstadoComprobanteFiltro) =>
    ['servicios', 'documentos', origen, estado] as const,
} as const;

export const serviciosApi = {
  /** Conexión con el controlador de surtidores (configuración + prueba en vivo). */
  pts: (signal?: AbortSignal): Promise<RespuestaPts> =>
    apiClient.get<RespuestaPts>('/api/servicios/pts', { signal }),

  /** Boleteo automático y cola de envío. */
  estado: (signal?: AbortSignal): Promise<RespuestaEstadoServicios> =>
    apiClient.get<RespuestaEstadoServicios>('/api/servicios/estado', { signal }),

  /** Fuerza una pasada del boleteo automático. */
  ejecutarBoleteo: (): Promise<RespuestaBoleteo> =>
    apiClient.post<RespuestaBoleteo>('/api/servicios/boleteo/ejecutar'),

  /** Detalle de comprobantes por origen y estado (`pendientes`/`rechazados`/`todos`). */
  documentos: (
    origen: OrigenComprobante,
    estado: EstadoComprobanteFiltro,
    signal?: AbortSignal,
  ): Promise<RespuestaDocumentosServicios> =>
    apiClient.get<RespuestaDocumentosServicios>(
      `/api/servicios/documentos?origen=${encodeURIComponent(origen)}&estado=${encodeURIComponent(estado)}`,
      { signal },
    ),
} as const;

/** Estado de la conexión con el controlador PTS. */
export function useServiciosPts() {
  return useQuery({
    queryKey: serviciosKeys.pts,
    queryFn: ({ signal }) => serviciosApi.pts(signal),
    staleTime: 30_000,
  });
}

/** Boleteo y cola de envío. */
export function useEstadoServicios() {
  return useQuery({
    queryKey: serviciosKeys.estado,
    queryFn: ({ signal }) => serviciosApi.estado(signal),
    staleTime: 15_000,
  });
}

/** Detalle de comprobantes por origen y estado. */
export function useDocumentosServicios(
  origen: OrigenComprobante,
  estado: EstadoComprobanteFiltro,
) {
  return useQuery({
    queryKey: serviciosKeys.documentos(origen, estado),
    queryFn: ({ signal }) => serviciosApi.documentos(origen, estado, signal),
    staleTime: 15_000,
  });
}

/** Acciones sobre los servicios. */
export function useMutarServicios() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: serviciosKeys.all });

  return {
    ejecutarBoleteo: useMutation({ mutationFn: serviciosApi.ejecutarBoleteo, onSuccess: refrescar }),
  };
}
